// AdminPanel.js - Admin-Verwaltung für Stunden, Aufträge, Mitarbeiter und Stundenarten
import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { API_BASE_URL } from '../config/apiConfig';
import { getWeekNumber } from '../utils/dateUtils';
import EntryHelpButton from './EntryHelpButton';
import { DEFAULT_ENTRY_HELP } from '../constants/helpText';
import CerpaxLogo from './CerpaxLogo';

const headerButtonStyle = { padding: '6px 12px', fontSize: '14px', lineHeight: '1.2' };
const formButtonRowStyle = { display: 'flex', gap: '10px', alignItems: 'stretch', marginTop: '10px' };
const formButtonStyle = {
  padding: '10px 18px',
  fontSize: '14px',
  lineHeight: '1.5',
  minHeight: '38px',
  height: '38px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontWeight: 'normal',
  boxSizing: 'border-box'
};

const ellipsisStyle = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
};

const stundenColumnStyles = {
  id: { width: '5ch', minWidth: '4ch', maxWidth: '6ch', ...ellipsisStyle },
  datum: { minWidth: '120px', maxWidth: '150px', ...ellipsisStyle },
  mitarbeiter: { maxWidth: '200px', ...ellipsisStyle },
  auftrag: { width: '110px', minWidth: '90px', maxWidth: '130px', ...ellipsisStyle },
  art: { maxWidth: '160px', ...ellipsisStyle },
  anzahl: { width: '60px', minWidth: '45px', maxWidth: '70px', textAlign: 'right', ...ellipsisStyle },
  beschreibung: { maxWidth: '250px', textAlign: 'left', ...ellipsisStyle },
  aktionen: { minWidth: '210px' }
};

const AdminPanel = ({ user, onBack }) => {
  const [activeTab, setActiveTab] = useState('stunden');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Stunden
  const [stunden, setStunden] = useState([]);
  const [editingStunde, setEditingStunde] = useState(null);
  const [showStundeForm, setShowStundeForm] = useState(false);

  // Filter für Stunden
  const [filterMitarbeiter, setFilterMitarbeiter] = useState('');
  const [filterKW, setFilterKW] = useState('');
  const [filterAuftrag, setFilterAuftrag] = useState('');

  // Aufträge
  const [auftraege, setAuftraege] = useState([]);
  const [showAuftragForm, setShowAuftragForm] = useState(false);
  const [editingAuftrag, setEditingAuftrag] = useState(null);
  const [filterAuftragStatus, setFilterAuftragStatus] = useState('active');

  // Mitarbeiter
  const [mitarbeiter, setMitarbeiter] = useState([]);
  const [showMitarbeiterForm, setShowMitarbeiterForm] = useState(false);
  const [editingMitarbeiter, setEditingMitarbeiter] = useState(null);

  // Stundenarten
  const [stundenarten, setStundenarten] = useState([]);
  const [showStundenartForm, setShowStundenartForm] = useState(false);
  const [editingStundenart, setEditingStundenart] = useState(null);

  // Passwort-Schutz für Neu-Anlage
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Hilfetext
  const [helpText, setHelpText] = useState('');
  const [helpStatus, setHelpStatus] = useState('');
  const [helpLoading, setHelpLoading] = useState(false);
  const [showMarkdownHelp, setShowMarkdownHelp] = useState(false);

  // Lade Daten basierend auf aktiven Tab
  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      switch (activeTab) {
        case 'stunden':
          await Promise.all([
            loadStunden(),
            loadAuftraege(),
            loadMitarbeiter(),
            loadStundenarten()
          ]);
          break;
        case 'auftraege':
          await loadAuftraege();
          break;
        case 'mitarbeiter':
          await loadMitarbeiter();
          break;
        case 'stundenarten':
          await loadStundenarten();
          break;
        case 'hilfe':
          await loadHelpText();
          break;
      }
    } catch (err) {
      setError(`Fehler beim Laden: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Helper für Tenant Header
  const getTenantHeaders = () => {
    return {
      'X-Tenant-Key': user.tenant || user.database,
      'Content-Type': 'application/json'
    };
  };

  // Stunden laden
  const loadStunden = async () => {
    const response = await fetch(`${API_BASE_URL}/tenant/arbeitszeiten`, {
      headers: getTenantHeaders()
    });
    if (!response.ok) throw new Error('Fehler beim Laden der Stunden');
    const result = await response.json();
    // Backend gibt { success: true, data: [...] } zurück
    setStunden(result.data || []);
  };

  // Aufträge laden
  const loadAuftraege = async (statusFilter) => {
    const filter = statusFilter || filterAuftragStatus;
    const queryParam = filter === 'active' ? '' : `?filter=${filter === 'inactive' ? 'inactive' : 'all'}`;
    const response = await fetch(`${API_BASE_URL}/tenant/tblAuftraege${queryParam}`, {
      headers: getTenantHeaders()
    });
    if (!response.ok) throw new Error('Fehler beim Laden der Aufträge');
    const data = await response.json();
    // Backend könnte Array oder {success, data} zurückgeben
    const items = Array.isArray(data) ? data : (data.data || []);
    // Map SQLite fields to expected format
    const mappedData = items.map(auf => ({
      id: auf.aufid,
      nummer: auf.aufnr,
      bezeichnung: auf.aufobjekt,
      aktiv: auf.aufaktiv === true || auf.aufaktiv === 1
    }));
    setAuftraege(mappedData);
  };

  // Mitarbeiter laden
  const loadMitarbeiter = async () => {
    const response = await fetch(`${API_BASE_URL}/tenant/mitarbeiter`, {
      headers: getTenantHeaders()
    });
    if (!response.ok) throw new Error('Fehler beim Laden der Mitarbeiter');
    const data = await response.json();
    // Backend könnte Array oder {success, data} zurückgeben
    const items = Array.isArray(data) ? data : (data.data || []);
    // Map SQLite fields to expected format
    // Hinweis: mitlogin = Benutzername (z.B. "joeh")
    const mappedData = items.map(mit => ({
      id: mit.mitid,
      nachname: mit.mitnachname,
      vorname: mit.mitvorname,
      benutzername: mit.mitlogin || ''  // mitlogin = Benutzername
    }));
    setMitarbeiter(mappedData);
  };

  // Stundenarten laden
  const loadStundenarten = async () => {
    const response = await fetch(`${API_BASE_URL}/tenant/tblSart`, {
      headers: getTenantHeaders()
    });
    if (!response.ok) throw new Error('Fehler beim Laden der Stundenarten');
    const data = await response.json();
    // Backend könnte Array oder {success, data} zurückgeben
    const items = Array.isArray(data) ? data : (data.data || []);
    // Map SQLite fields to expected format
    const mappedData = items.map(art => ({
      id: art.sart,
      bezeichnung: art.sarbezeichnung,
      stundensatz: art.sarsatz || 0
    }));
    setStundenarten(mappedData);
  };

  // Hilfetext laden
  const loadHelpText = async () => {
    setHelpLoading(true);
    setHelpStatus('');
    try {
      const response = await fetch(`${API_BASE_URL}/help/entry`, {
        headers: getTenantHeaders()
      });
      if (!response.ok) {
        throw new Error('Hilfetext konnte nicht geladen werden');
      }
      const data = await response.json();
      setHelpText(data.content || DEFAULT_ENTRY_HELP);
      setError(null);
    } catch (err) {
      console.error('Fehler beim Laden des Hilfetextes:', err);
      setHelpText(DEFAULT_ENTRY_HELP);
    } finally {
      setHelpLoading(false);
    }
  };

  const handleSaveHelpText = async () => {
    setHelpStatus('');
    setHelpLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/help/entry`, {
        method: 'PUT',
        headers: getTenantHeaders(),
        body: JSON.stringify({ content: helpText })
      });
      if (!response.ok) throw new Error('Hilfetext konnte nicht gespeichert werden');
      setHelpStatus('Hilfetext gespeichert.');
    } catch (err) {
      setError(`Fehler beim Speichern des Hilfetextes: ${err.message}`);
    } finally {
      setHelpLoading(false);
    }
  };

  // Stunde bearbeiten
  const handleEditStunde = (stunde) => {
    setEditingStunde({
      stuwebid: stunde.stuwebid,
      studatum: stunde.studatum ? stunde.studatum.split('T')[0] : '',
      stuanzahl: stunde.stuanzahl,
      stubeschrieb: stunde.stubeschrieb || '',
      stuaufidref: stunde.stuaufidref ?? '',
      stusaridref: stunde.stusaridref ?? '',
      stumitidref: stunde.stumitidref ?? ''
    });
    setShowStundeForm(false);
  };

  const handleSaveStunde = async (stundeData) => {
    try {
      const isNew = !stundeData.stuwebid;
      const url = isNew
        ? `${API_BASE_URL}/tenant/arbeitszeiten`
        : `${API_BASE_URL}/tenant/arbeitszeiten/${stundeData.stuwebid}`;
      const method = isNew ? 'POST' : 'PUT';

      const requestBody = {
        stumitidref: parseInt(stundeData.stumitidref, 10),
        stuaufidref: parseInt(stundeData.stuaufidref, 10),
        stusaridref: parseInt(stundeData.stusaridref, 10),
        studatum: stundeData.studatum,
        stuanzahl: parseFloat(stundeData.stuanzahl),
        stubeschrieb: stundeData.stubeschrieb || '',
      };

      // Für neue Stunden zusätzliche Felder setzen
      if (isNew) {
        requestBody.stuverrechnet = false;
        requestBody.stuzuschlag = 0;
        requestBody.stumank = false;
      }

      const response = await fetch(url, {
        method,
        headers: getTenantHeaders(),
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        // WICHTIG: Body-Stream kann nur einmal gelesen werden!
        let errorData = {};
        try {
          const errorText = await response.text();
          try {
            errorData = JSON.parse(errorText);
          } catch (parseError) {
            errorData = { message: errorText || response.statusText };
          }
        } catch (readError) {
          errorData = { message: response.statusText };
        }
        throw new Error(errorData.message || `Fehler beim ${isNew ? 'Erstellen' : 'Aktualisieren'} der Stunde`);
      }

      await loadStunden();
      setEditingStunde(null);
      setShowStundeForm(false);
    } catch (err) {
      setError(`Fehler beim Speichern: ${err.message}`);
    }
  };

  // Stunde löschen
  const handleDeleteStunde = async (id) => {
    if (!window.confirm('Möchten Sie diese Stunde wirklich löschen?')) return;
    try {
      const response = await fetch(`${API_BASE_URL}/tenant/arbeitszeiten/${id}`, {
        method: 'DELETE',
        headers: getTenantHeaders()
      });
      if (!response.ok) throw new Error('Fehler beim Löschen');
      await loadStunden();
    } catch (err) {
      setError(`Fehler beim Löschen: ${err.message}`);
    }
  };

  // Auftrag speichern
  const handleSaveAuftrag = async (auftrag) => {
    try {
      const url = editingAuftrag
        ? `${API_BASE_URL}/tenant/tblAuftraege/${editingAuftrag.id}`
        : `${API_BASE_URL}/tenant/tblAuftraege`;
      const method = editingAuftrag ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: getTenantHeaders(),
        body: JSON.stringify({
          nummer: auftrag.nummer,
          bezeichnung: auftrag.bezeichnung,
          aktiv: auftrag.aktiv !== undefined ? auftrag.aktiv : true
        })
      });

      if (!response.ok) throw new Error('Fehler beim Speichern');
      await loadAuftraege();
      setShowAuftragForm(false);
      setEditingAuftrag(null);
    } catch (err) {
      setError(`Fehler beim Speichern: ${err.message}`);
    }
  };

  // Auftrag löschen
  const handleDeleteAuftrag = async (id) => {
    if (!window.confirm('Möchten Sie diesen Auftrag wirklich löschen?')) return;
    try {
      const response = await fetch(`${API_BASE_URL}/tenant/tblAuftraege/${id}`, {
        method: 'DELETE',
        headers: getTenantHeaders()
      });
      if (!response.ok) throw new Error('Fehler beim Löschen');
      await loadAuftraege();
    } catch (err) {
      setError(`Fehler beim Löschen: ${err.message}`);
    }
  };

  // Mitarbeiter speichern
  const handleSaveMitarbeiter = async (mitarbeiter) => {
    try {
      const url = editingMitarbeiter
        ? `${API_BASE_URL}/tenant/mitarbeiter/${editingMitarbeiter.id}`
        : `${API_BASE_URL}/tenant/mitarbeiter`;
      const method = editingMitarbeiter ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: getTenantHeaders(),
        body: JSON.stringify({
          nachname: mitarbeiter.nachname,
          vorname: mitarbeiter.vorname,
          login: mitarbeiter.benutzername,  // benutzername wird als mitlogin gespeichert
          passwort: mitarbeiter.passwort || undefined
        })
      });

      if (!response.ok) throw new Error('Fehler beim Speichern');
      await loadMitarbeiter();
      setShowMitarbeiterForm(false);
      setEditingMitarbeiter(null);
    } catch (err) {
      setError(`Fehler beim Speichern: ${err.message}`);
    }
  };

  // Mitarbeiter löschen
  const handleDeleteMitarbeiter = async (id) => {
    if (!window.confirm('Möchten Sie diesen Mitarbeiter wirklich löschen?')) return;
    try {
      const response = await fetch(`${API_BASE_URL}/tenant/mitarbeiter/${id}`, {
        method: 'DELETE',
        headers: getTenantHeaders()
      });
      if (!response.ok) throw new Error('Fehler beim Löschen');
      await loadMitarbeiter();
    } catch (err) {
      setError(`Fehler beim Löschen: ${err.message}`);
    }
  };

  // Stundenart speichern
  const handleSaveStundenart = async (stundenart) => {
    try {
      const url = editingStundenart
        ? `${API_BASE_URL}/tenant/tblSart/${editingStundenart.id}`
        : `${API_BASE_URL}/tenant/tblSart`;
      const method = editingStundenart ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: getTenantHeaders(),
        body: JSON.stringify({
          bezeichnung: stundenart.bezeichnung,
          stundensatz: parseFloat(stundenart.stundensatz) || 0
        })
      });

      if (!response.ok) throw new Error('Fehler beim Speichern');
      await loadStundenarten();
      setShowStundenartForm(false);
      setEditingStundenart(null);
    } catch (err) {
      setError(`Fehler beim Speichern: ${err.message}`);
    }
  };

  // Stundenart löschen
  const handleDeleteStundenart = async (id) => {
    if (!window.confirm('Möchten Sie diese Stundenart wirklich löschen?')) return;
    try {
      const response = await fetch(`${API_BASE_URL}/tenant/tblSart/${id}`, {
        method: 'DELETE',
        headers: getTenantHeaders()
      });
      if (!response.ok) throw new Error('Fehler beim Löschen');
      await loadStundenarten();
    } catch (err) {
      setError(`Fehler beim Löschen: ${err.message}`);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE');
  };

  // Passwort-geschützte Aktion starten
  const requestProtectedAction = (actionType) => {
    setPendingAction(actionType);
    setPasswordInput('');
    setPasswordError('');
    setShowPasswordModal(true);
  };

  // Passwort prüfen und Aktion ausführen
  const handlePasswordSubmit = () => {
    if (passwordInput === 'allesklar') {
      setShowPasswordModal(false);
      setPasswordInput('');
      setPasswordError('');

      // Aktion ausführen basierend auf pendingAction
      switch (pendingAction) {
        case 'neueStunde':
          setEditingStunde(null);
          setShowStundeForm(true);
          break;
        case 'neuerAuftrag':
          setEditingAuftrag(null);
          setShowAuftragForm(true);
          break;
        case 'neuerMitarbeiter':
          setEditingMitarbeiter(null);
          setShowMitarbeiterForm(true);
          break;
        case 'neueStundenart':
          setEditingStundenart(null);
          setShowStundenartForm(true);
          break;
        default:
          break;
      }
      setPendingAction(null);
    } else {
      setPasswordError('Falsches Passwort');
    }
  };

  return (
    <div className="admin-panel" style={{ padding: '20px', maxWidth: '1200px', width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '15px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <CerpaxLogo className="app-logo" />
          <h1 style={{ margin: 0 }}>Admin Panel</h1>
        </div>
        <button
          onClick={onBack}
          className="btn btn-logout"
          style={{
            ...headerButtonStyle,
            minWidth: '110px',
            maxWidth: '110px',
            padding: '4px 10px',
            fontSize: '13px',
            textAlign: 'center',
            alignSelf: 'flex-end',
            flexShrink: 0
          }}
        >
          Abmelden
        </button>
      </div>

      {error && (
        <div className="error-message" style={{ padding: '10px', background: '#fee', color: '#c00', marginBottom: '20px', borderRadius: '4px' }}>
          {error}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '2px solid #ddd', flexWrap: 'wrap' }}>
        {['stunden', 'auftraege', 'mitarbeiter', 'stundenarten', 'hilfe'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '10px 20px',
              border: 'none',
              background: activeTab === tab ? '#007bff' : 'transparent',
              color: activeTab === tab ? 'white' : '#333',
              cursor: 'pointer',
              borderBottom: activeTab === tab ? '2px solid #007bff' : 'none',
              marginBottom: '-2px',
              minWidth: '100px',
              textAlign: 'center'
            }}
          >
            {tab === 'stunden' ? 'Stunden' :
             tab === 'auftraege' ? 'Aufträge' :
             tab === 'mitarbeiter' ? 'Mitarbeiter' :
             tab === 'stundenarten' ? 'Stundenarten' : 'Hilfe'}
          </button>
        ))}
      </div>

      {loading && <div>Lade Daten...</div>}

      {/* Stunden Tab */}
      {activeTab === 'stunden' && !loading && (
        <div>
          <h2 style={{ marginBottom: '10px' }}>Alle Stunden</h2>
          <div style={{ marginBottom: '20px' }}>
            <button
              onClick={() => requestProtectedAction('neueStunde')}
              className="btn btn-primary"
              style={headerButtonStyle}
            >
              Neue Stunde
            </button>
          </div>

          {/* Filter */}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', flexWrap: 'wrap', alignItems: 'center' }}>
            <select
              value={filterMitarbeiter}
              onChange={e => setFilterMitarbeiter(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #ccc', minWidth: '160px' }}
            >
              <option value="">Alle Mitarbeiter</option>
              {mitarbeiter.map(m => (
                <option key={m.id} value={m.id}>{m.nachname} {m.vorname}</option>
              ))}
            </select>

            <select
              value={filterKW}
              onChange={e => setFilterKW(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #ccc', minWidth: '120px' }}
            >
              <option value="">Alle KW</option>
              {(() => {
                const kwSet = new Set();
                stunden.forEach(s => {
                  if (s.studatum) {
                    const d = new Date(s.studatum);
                    const year = d.getFullYear();
                    const kw = getWeekNumber(d);
                    kwSet.add(`${year}-${kw}`);
                  }
                });
                return Array.from(kwSet)
                  .sort((a, b) => b.localeCompare(a))
                  .map(k => {
                    const [year, kw] = k.split('-');
                    return <option key={k} value={k}>KW {kw} / {year}</option>;
                  });
              })()}
            </select>

            <select
              value={filterAuftrag}
              onChange={e => setFilterAuftrag(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #ccc', minWidth: '160px' }}
            >
              <option value="">Alle Aufträge</option>
              {auftraege.map(a => (
                <option key={a.id} value={a.id}>{a.nummer} - {a.bezeichnung}</option>
              ))}
            </select>

            {(filterMitarbeiter || filterKW || filterAuftrag) && (
              <button
                onClick={() => { setFilterMitarbeiter(''); setFilterKW(''); setFilterAuftrag(''); }}
                style={{ padding: '6px 12px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              >
                Filter zurücksetzen
              </button>
            )}
          </div>
          {(editingStunde || showStundeForm) && (
            <div style={{
              position: 'fixed',
              top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000
            }}>
              <div style={{
                background: 'white',
                padding: '30px',
                borderRadius: '8px',
                maxWidth: '600px',
                width: '90%',
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                maxHeight: '90vh',
                overflowY: 'auto'
              }}>
                <StundenEditForm
                  stunde={editingStunde}
                  auftraege={auftraege}
                  stundenarten={stundenarten}
                  mitarbeiter={mitarbeiter}
                  user={user}
                  onSave={handleSaveStunde}
                  onCancel={() => {
                    setEditingStunde(null);
                    setShowStundeForm(false);
                  }}
                />
              </div>
            </div>
          )}
          {(() => {
            const filtered = stunden.filter(stunde => {
              if (filterMitarbeiter && stunde.stumitidref !== parseInt(filterMitarbeiter)) return false;
              if (filterAuftrag && stunde.stuaufidref !== parseInt(filterAuftrag)) return false;
              if (filterKW && stunde.studatum) {
                const d = new Date(stunde.studatum);
                const kwKey = `${d.getFullYear()}-${getWeekNumber(d)}`;
                if (kwKey !== filterKW) return false;
              }
              return true;
            });
            // Gruppierung nach Stundenart
            const perArt = {};
            filtered.forEach(s => {
              const art = s.sarbezeichnung || 'Unbekannt';
              perArt[art] = (perArt[art] || 0) + (parseFloat(s.stuanzahl) || 0);
            });
            return (
              <div style={{ marginBottom: '10px', fontWeight: '600', fontSize: '15px' }}>
                <span>{filtered.length} Einträge | {Object.entries(perArt)
                  .sort((a, b) => b[1] - a[1])
                  .map(([art, h]) => `${art}: ${h.toFixed(2)}`)
                  .join(' | ')}</span>
              </div>
            );
          })()}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f5f5f5' }}>
                  <th style={{ padding: '10px', textAlign: 'left', border: '1px solid #ddd', color: '#000', ...stundenColumnStyles.id }}>ID</th>
                  <th style={{ padding: '10px', textAlign: 'left', border: '1px solid #ddd', color: '#000', ...stundenColumnStyles.datum }}>Datum</th>
                  <th style={{ padding: '10px', textAlign: 'left', border: '1px solid #ddd', color: '#000', ...stundenColumnStyles.mitarbeiter }}>Mitarbeiter</th>
                  <th style={{ padding: '10px', textAlign: 'left', border: '1px solid #ddd', color: '#000', ...stundenColumnStyles.auftrag }}>Auftrag</th>
                  <th style={{ padding: '10px', textAlign: 'left', border: '1px solid #ddd', color: '#000', ...stundenColumnStyles.art }}>Stundenart</th>
                  <th style={{ padding: '10px', textAlign: 'right', border: '1px solid #ddd', color: '#000', ...stundenColumnStyles.anzahl }}>Anzahl</th>
                  <th style={{ padding: '10px', textAlign: 'left', border: '1px solid #ddd', color: '#000', ...stundenColumnStyles.beschreibung }}>Beschreibung</th>
                  <th style={{ padding: '10px', textAlign: 'center', border: '1px solid #ddd', color: '#000', ...stundenColumnStyles.aktionen }}>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {stunden.filter(stunde => {
                  if (filterMitarbeiter && stunde.stumitidref !== parseInt(filterMitarbeiter)) return false;
                  if (filterAuftrag && stunde.stuaufidref !== parseInt(filterAuftrag)) return false;
                  if (filterKW && stunde.studatum) {
                    const d = new Date(stunde.studatum);
                    const kwKey = `${d.getFullYear()}-${getWeekNumber(d)}`;
                    if (kwKey !== filterKW) return false;
                  }
                  return true;
                }).map(stunde => (
                  <tr key={stunde.stuwebid}>
                    <td style={{ padding: '10px', border: '1px solid #ddd', ...stundenColumnStyles.id }}>{stunde.stuwebid}</td>
                    <td style={{ padding: '10px', border: '1px solid #ddd', ...stundenColumnStyles.datum }}>{formatDate(stunde.studatum)}</td>
                    <td style={{ padding: '10px', border: '1px solid #ddd', ...stundenColumnStyles.mitarbeiter }}>{stunde.mitarbeitername || '-'}</td>
                    <td style={{ padding: '10px', border: '1px solid #ddd', ...stundenColumnStyles.auftrag }}>{stunde.aufnr || '-'}</td>
                    <td style={{ padding: '10px', border: '1px solid #ddd', ...stundenColumnStyles.art }}>{stunde.sarbezeichnung || '-'}</td>
                    <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right', ...stundenColumnStyles.anzahl }}>{parseFloat(stunde.stuanzahl).toFixed(2)}</td>
                    <td style={{ padding: '10px', border: '1px solid #ddd', ...stundenColumnStyles.beschreibung }}>{stunde.stubeschrieb || '-'}</td>
                    <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center', ...stundenColumnStyles.aktionen }}>
                      <div style={{ display: 'inline-flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                        <button
                          onClick={() => handleEditStunde(stunde)}
                          aria-label="Stunde bearbeiten"
                          title="Bearbeiten"
                          style={{ padding: '5px 10px', background: '#0d6efd', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', whiteSpace: 'nowrap', width: '36px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDeleteStunde(stunde.stuwebid)}
                          aria-label="Stunde löschen"
                          title="Löschen"
                          style={{ padding: '5px 10px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', whiteSpace: 'nowrap', width: '36px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Aufträge Tab */}
      {activeTab === 'auftraege' && !loading && (
        <div>
          <h2 style={{ marginBottom: '10px' }}>Aufträge</h2>
          <div style={{ marginBottom: '20px', display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => requestProtectedAction('neuerAuftrag')}
              className="btn btn-primary"
              style={headerButtonStyle}
            >
              Neuer Auftrag
            </button>
            <select
              value={filterAuftragStatus}
              onChange={(e) => {
                setFilterAuftragStatus(e.target.value);
                loadAuftraege(e.target.value);
              }}
              style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
            >
              <option value="active">Nur aktive</option>
              <option value="inactive">Nur inaktive</option>
              <option value="all">Alle</option>
            </select>
          </div>

          {showAuftragForm && (
            <div style={{
              position: 'fixed',
              top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000
            }}>
              <div style={{
                background: 'white',
                padding: '30px',
                borderRadius: '8px',
                maxWidth: '500px',
                width: '90%',
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
              }}>
                <AuftragForm
                  auftrag={editingAuftrag}
                  onSave={handleSaveAuftrag}
                  onCancel={() => {
                    setShowAuftragForm(false);
                    setEditingAuftrag(null);
                  }}
                />
              </div>
            </div>
          )}

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f5f5f5' }}>
                  <th style={{ padding: '10px', textAlign: 'left', border: '1px solid #ddd', color: '#000' }}>ID</th>
                  <th style={{ padding: '10px', textAlign: 'left', border: '1px solid #ddd', color: '#000' }}>Nummer</th>
                  <th style={{ padding: '10px', textAlign: 'left', border: '1px solid #ddd', color: '#000' }}>Bezeichnung</th>
                  <th style={{ padding: '10px', textAlign: 'center', border: '1px solid #ddd', color: '#000' }}>Aktiv</th>
                  <th style={{ padding: '10px', textAlign: 'center', border: '1px solid #ddd', color: '#000' }}>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {auftraege.map(auftrag => (
                  <tr key={auftrag.id}>
                    <td style={{ padding: '10px', border: '1px solid #ddd' }}>{auftrag.id}</td>
                    <td style={{ padding: '10px', border: '1px solid #ddd' }}>{auftrag.nummer}</td>
                    <td style={{ padding: '10px', border: '1px solid #ddd' }}>{auftrag.bezeichnung}</td>
                    <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>
                      {auftrag.aktiv ? '✓' : '✗'}
                    </td>
                    <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>
                      <div style={{ display: 'inline-flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
                        <button
                          onClick={() => {
                            setEditingAuftrag(auftrag);
                            setShowAuftragForm(true);
                          }}
                          aria-label="Auftrag bearbeiten"
                          title="Bearbeiten"
                          style={{ padding: '5px', background: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', width: '36px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDeleteAuftrag(auftrag.id)}
                          aria-label="Auftrag löschen"
                          title="Löschen"
                          style={{ padding: '5px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', width: '36px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Mitarbeiter Tab */}
      {activeTab === 'mitarbeiter' && !loading && (
        <div>
          <h2 style={{ marginBottom: '10px' }}>Mitarbeiter</h2>
          <div style={{ marginBottom: '20px' }}>
            <button
              onClick={() => requestProtectedAction('neuerMitarbeiter')}
              className="btn btn-primary"
              style={headerButtonStyle}
            >
              Neuer Mitarbeiter
            </button>
          </div>

          {showMitarbeiterForm && (
            <div style={{
              position: 'fixed',
              top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000
            }}>
              <div style={{
                background: 'white',
                padding: '30px',
                borderRadius: '8px',
                maxWidth: '500px',
                width: '90%',
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
              }}>
                <MitarbeiterForm
                  mitarbeiter={editingMitarbeiter}
                  onSave={handleSaveMitarbeiter}
                  onCancel={() => {
                    setShowMitarbeiterForm(false);
                    setEditingMitarbeiter(null);
                  }}
                />
              </div>
            </div>
          )}

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f5f5f5' }}>
                  <th style={{ padding: '10px', textAlign: 'left', border: '1px solid #ddd', color: '#000' }}>ID</th>
                  <th style={{ padding: '10px', textAlign: 'left', border: '1px solid #ddd', color: '#000' }}>Nachname</th>
                  <th style={{ padding: '10px', textAlign: 'left', border: '1px solid #ddd', color: '#000' }}>Vorname</th>
                  <th style={{ padding: '10px', textAlign: 'left', border: '1px solid #ddd', color: '#000' }}>Login</th>
                  <th style={{ padding: '10px', textAlign: 'center', border: '1px solid #ddd', color: '#000' }}>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {mitarbeiter.map(mit => (
                  <tr key={mit.id}>
                    <td style={{ padding: '10px', border: '1px solid #ddd' }}>{mit.id}</td>
                    <td style={{ padding: '10px', border: '1px solid #ddd' }}>{mit.nachname}</td>
                    <td style={{ padding: '10px', border: '1px solid #ddd' }}>{mit.vorname}</td>
                    <td style={{ padding: '10px', border: '1px solid #ddd' }}>{mit.benutzername}</td>
                    <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>
                      <div style={{ display: 'inline-flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
                        <button
                          onClick={() => {
                            setEditingMitarbeiter(mit);
                            setShowMitarbeiterForm(true);
                          }}
                          aria-label="Mitarbeiter bearbeiten"
                          title="Bearbeiten"
                          style={{ padding: '5px', background: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', width: '36px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDeleteMitarbeiter(mit.id)}
                          aria-label="Mitarbeiter löschen"
                          title="Löschen"
                          style={{ padding: '5px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', width: '36px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Stundenarten Tab */}
      {activeTab === 'stundenarten' && !loading && (
        <div>
          <h2 style={{ marginBottom: '10px' }}>Stundenarten</h2>
          <div style={{ marginBottom: '20px' }}>
            <button
              onClick={() => requestProtectedAction('neueStundenart')}
              className="btn btn-primary"
              style={headerButtonStyle}
            >
              Neue Stundenart
            </button>
          </div>

          {showStundenartForm && (
            <div style={{
              position: 'fixed',
              top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000
            }}>
              <div style={{
                background: 'white',
                padding: '30px',
                borderRadius: '8px',
                maxWidth: '500px',
                width: '90%',
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
              }}>
                <StundenartForm
                  stundenart={editingStundenart}
                  onSave={handleSaveStundenart}
                  onCancel={() => {
                    setShowStundenartForm(false);
                    setEditingStundenart(null);
                  }}
                />
              </div>
            </div>
          )}

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f5f5f5' }}>
                  <th style={{ padding: '10px', textAlign: 'left', border: '1px solid #ddd', color: '#000' }}>ID</th>
                  <th style={{ padding: '10px', textAlign: 'left', border: '1px solid #ddd', color: '#000' }}>Bezeichnung</th>
                  <th style={{ padding: '10px', textAlign: 'right', border: '1px solid #ddd', color: '#000' }}>Stundensatz</th>
                  <th style={{ padding: '10px', textAlign: 'center', border: '1px solid #ddd', color: '#000' }}>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {stundenarten.map(art => (
                  <tr key={art.id}>
                    <td style={{ padding: '10px', border: '1px solid #ddd' }}>{art.id}</td>
                    <td style={{ padding: '10px', border: '1px solid #ddd' }}>{art.bezeichnung}</td>
                    <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right' }}>{parseFloat(art.stundensatz).toFixed(2)}</td>
                    <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>
                      <div style={{ display: 'inline-flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
                        <button
                          onClick={() => {
                            setEditingStundenart(art);
                            setShowStundenartForm(true);
                          }}
                          aria-label="Stundenart bearbeiten"
                          title="Bearbeiten"
                          style={{ padding: '5px', background: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', width: '36px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDeleteStundenart(art.id)}
                          aria-label="Stundenart löschen"
                          title="Löschen"
                          style={{ padding: '5px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', width: '36px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Hilfe Tab */}
      {activeTab === 'hilfe' && !loading && (
        <div style={{ minWidth: '100%' }}>
          <h2 style={{ marginBottom: '10px' }}>Hilfetext für die Erfassungsmaske</h2>
          <p style={{ marginBottom: '15px', color: '#555' }}>
            Dieser Text wird in der Stunden-Erfassungsmaske angezeigt, wenn Benutzer auf „Hilfe" klicken.
            Der Inhalt kann als Markdown gepflegt werden.
          </p>
          
          {/* Markdown-Hilfe */}
          <div style={{ marginBottom: '15px' }}>
            <button
              type="button"
              onClick={() => setShowMarkdownHelp(!showMarkdownHelp)}
              style={{
                padding: '8px 16px',
                background: '#17a2b8',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <span>{showMarkdownHelp ? '▼' : '▶'}</span>
              <span>Markdown-Formatierungshilfe</span>
            </button>
            
            {showMarkdownHelp && (
              <div style={{
                marginTop: '10px',
                padding: '15px',
                background: '#f0f9ff',
                border: '1px solid #0ea5e9',
                borderRadius: '4px',
                fontSize: '14px',
                boxSizing: 'border-box',
                overflow: 'hidden'
              }}>
                <h4 style={{ marginTop: 0, marginBottom: '12px', color: '#0369a1' }}>Verfügbare Markdown-Formatierungen:</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '15px' }}>
                  <div>
                    <strong>Überschriften:</strong>
                    <pre style={{ background: '#fff', padding: '8px', borderRadius: '4px', fontSize: '12px', marginTop: '5px', overflow: 'hidden', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
{`# Überschrift 1
## Überschrift 2
### Überschrift 3`}
                    </pre>
                  </div>
                  <div>
                    <strong>Textformatierung:</strong>
                    <pre style={{ background: '#fff', padding: '8px', borderRadius: '4px', fontSize: '12px', marginTop: '5px', overflow: 'hidden', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
{`**fett** oder __fett__
*kursiv* oder _kursiv_
***fett und kursiv***`}
                    </pre>
                  </div>
                  <div>
                    <strong>Listen:</strong>
                    <pre style={{ background: '#fff', padding: '8px', borderRadius: '4px', fontSize: '12px', marginTop: '5px', overflow: 'hidden', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
{`- Aufzählung 1
- Aufzählung 2
  - Unterpunkt

1. Nummeriert 1
2. Nummeriert 2`}
                    </pre>
                  </div>
                  <div>
                    <strong>Code:</strong>
                    <pre style={{ background: '#fff', padding: '8px', borderRadius: '4px', fontSize: '12px', marginTop: '5px', overflow: 'hidden', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
{`\`Inline-Code\`

\`\`\`
Code-Block
mehrere Zeilen
\`\`\``}
                    </pre>
                  </div>
                </div>
                <div style={{ marginTop: '15px', padding: '10px', background: '#fff', borderRadius: '4px' }}>
                  <strong>Beispiel:</strong>
                  <pre style={{ marginTop: '8px', fontSize: '12px', whiteSpace: 'pre-wrap' }}>
{`# Hilfe zur Stundenerfassung

## Datum
Wähle das Datum **innerhalb der letzten 10 Tage**.

## Stunden
- Mindestens **0.5 Stunden**
- In 0.5er-Schritten

Bei Fragen wende dich an die Administration.`}
                  </pre>
                </div>
              </div>
            )}
          </div>
          
          <textarea
            value={helpText}
            onChange={(e) => setHelpText(e.target.value)}
            rows={14}
            style={{ width: '100%', padding: '12px', borderRadius: '4px', border: '1px solid #ddd', fontFamily: 'monospace', boxSizing: 'border-box' }}
            placeholder="Markdown-Hilfetext hier eingeben..."
          />
          <div style={{ ...formButtonRowStyle, justifyContent: 'flex-start' }}>
            <button
              onClick={handleSaveHelpText}
              style={{ ...formButtonStyle, background: '#007bff', color: 'white' }}
              disabled={helpLoading}
            >
              {helpLoading ? 'Speichern...' : 'Hilfetext speichern'}
            </button>
            {helpStatus && (
              <span style={{ color: '#15803d', fontSize: '14px', alignSelf: 'center' }}>
                {helpStatus}
              </span>
            )}
          </div>
          <div style={{ marginTop: '20px' }}>
            <h3>Vorschau</h3>
            <div className="help-panel" style={{ background: '#fefce8', border: '1px solid #fcd34d', borderRadius: '4px', padding: '12px', textAlign: 'left' }}>
              {helpText ? (
                <ReactMarkdown>{helpText}</ReactMarkdown>
              ) : (
                <span style={{ color: '#6b7280' }}>Noch kein Hilfetext hinterlegt.</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Passwort-Modal für geschützte Aktionen */}
      {showPasswordModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '8px',
            maxWidth: '450px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }}>
            <h3 style={{ marginTop: 0, color: '#dc3545' }}>Achtung!</h3>
            <p style={{
              background: '#fff3cd',
              border: '1px solid #ffc107',
              padding: '12px',
              borderRadius: '4px',
              color: '#856404',
              marginBottom: '20px'
            }}>
              Änderungen müssen in Cerpax Access erfolgen. Änderungen hier nur wenn Konsequenz klar ist.
            </p>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Passwort für Weiter:
              </label>
              <input
                type="password"
                value={passwordInput}
                onChange={(e) => {
                  setPasswordInput(e.target.value);
                  setPasswordError('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handlePasswordSubmit();
                }}
                autoFocus
                style={{
                  width: '100%',
                  padding: '10px',
                  border: passwordError ? '2px solid #dc3545' : '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '16px',
                  boxSizing: 'border-box'
                }}
              />
              {passwordError && (
                <div style={{ color: '#dc3545', marginTop: '5px', fontSize: '14px' }}>
                  {passwordError}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordInput('');
                  setPasswordError('');
                  setPendingAction(null);
                }}
                style={{
                  padding: '10px 20px',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Abbrechen
              </button>
              <button
                onClick={handlePasswordSubmit}
                style={{
                  padding: '10px 20px',
                  background: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Weiter
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Auftrag Formular
const AuftragForm = ({ auftrag, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    nummer: auftrag?.nummer || '',
    bezeichnung: auftrag?.bezeichnung || '',
    aktiv: auftrag?.aktiv !== undefined ? auftrag.aktiv : true
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div>
      <h3 style={{ marginTop: 0 }}>{auftrag ? 'Auftrag bearbeiten' : 'Neuer Auftrag'}</h3>
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px' }}>Nummer:</label>
          <input
            type="text"
            value={formData.nummer}
            onChange={(e) => setFormData({ ...formData, nummer: e.target.value })}
            required
            style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
          />
        </div>
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px' }}>Bezeichnung:</label>
          <input
            type="text"
            value={formData.bezeichnung}
            onChange={(e) => setFormData({ ...formData, bezeichnung: e.target.value })}
            required
            style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
          />
        </div>
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'flex', alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={formData.aktiv}
              onChange={(e) => setFormData({ ...formData, aktiv: e.target.checked })}
              style={{ marginRight: '8px' }}
            />
            Aktiv
          </label>
        </div>
        <div style={formButtonRowStyle}>
          <button 
            type="submit" 
            style={{
              ...formButtonStyle,
              background: '#007bff',
              color: 'white'
            }}
          >
            Speichern
          </button>
          <button 
            type="button" 
            onClick={onCancel}
            style={{
              ...formButtonStyle,
              background: '#6c757d',
              color: 'white'
            }}
          >
            Abbrechen
          </button>
        </div>
      </form>
    </div>
  );
};

// Mitarbeiter Formular
const MitarbeiterForm = ({ mitarbeiter, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    nachname: mitarbeiter?.nachname || '',
    vorname: mitarbeiter?.vorname || '',
    benutzername: mitarbeiter?.benutzername || '',
    passwort: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div>
      <h3 style={{ marginTop: 0 }}>{mitarbeiter ? 'Mitarbeiter bearbeiten' : 'Neuer Mitarbeiter'}</h3>
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px' }}>Nachname:</label>
          <input
            type="text"
            value={formData.nachname}
            onChange={(e) => setFormData({ ...formData, nachname: e.target.value })}
            required
            style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
          />
        </div>
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px' }}>Vorname:</label>
          <input
            type="text"
            value={formData.vorname}
            onChange={(e) => setFormData({ ...formData, vorname: e.target.value })}
            required
            style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
          />
        </div>
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px' }}>Benutzername:</label>
          <input
            type="text"
            value={formData.benutzername}
            onChange={(e) => setFormData({ ...formData, benutzername: e.target.value })}
            placeholder="z.B. joeh (wird automatisch generiert bei Sync)"
            style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
          />
          <small style={{ color: '#666', fontSize: '12px' }}>
            Format: erste 2 Buchstaben Vorname + erste 2 Buchstaben Nachname (z.B. Joel Ehrat → joeh)
          </small>
        </div>
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px' }}>
            Passwort {mitarbeiter ? '(leer lassen, um nicht zu ändern)' : ''}:
          </label>
          <input
            type="password"
            value={formData.passwort}
            onChange={(e) => setFormData({ ...formData, passwort: e.target.value })}
            required={!mitarbeiter}
            style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
          />
        </div>
        <div style={formButtonRowStyle}>
          <button
            type="submit"
            style={{
              ...formButtonStyle,
              background: '#007bff',
              color: 'white'
            }}
          >
            Speichern
          </button>
          <button
            type="button"
            onClick={onCancel}
            style={{
              ...formButtonStyle,
              background: '#6c757d',
              color: 'white'
            }}
          >
            Abbrechen
          </button>
        </div>
      </form>
    </div>
  );
};

// Stunden Formular (Bearbeiten)
const StundenEditForm = ({ stunde, auftraege, stundenarten, mitarbeiter, user, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    stuwebid: stunde?.stuwebid || '',
    studatum: stunde?.studatum || '',
    stumitidref: stunde?.stumitidref || '',
    stuaufidref: stunde?.stuaufidref || '',
    stusaridref: stunde?.stusaridref || '',
    stuanzahl: stunde?.stuanzahl || 0,
    stubeschrieb: stunde?.stubeschrieb || ''
  });

  useEffect(() => {
    setFormData({
      stuwebid: stunde?.stuwebid || '',
      studatum: stunde?.studatum ? stunde.studatum.split('T')[0] : '',
      stumitidref: stunde?.stumitidref || '',
      stuaufidref: stunde?.stuaufidref || '',
      stusaridref: stunde?.stusaridref || '',
      stuanzahl: stunde?.stuanzahl || 0,
      stubeschrieb: stunde?.stubeschrieb || ''
    });
  }, [stunde]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const isNew = !stunde || !stunde.stuwebid;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
        <h3 style={{ margin: 0 }}>{isNew ? 'Neue Stunde erfassen' : `Stunde bearbeiten (ID ${stunde.stuwebid})`}</h3>
        <div style={{ maxWidth: '200px' }}>
          <EntryHelpButton buttonClassName="btn btn-secondary" tenant={user?.tenant || user?.database} />
        </div>
      </div>
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '15px' }}>
          <div style={{ flex: '1 1 200px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>Datum:</label>
            <input
              type="date"
              value={formData.studatum}
              onChange={(e) => setFormData({ ...formData, studatum: e.target.value })}
              required
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>
          <div style={{ flex: '1 1 200px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>Mitarbeiter:</label>
            <select
              value={formData.stumitidref}
              onChange={(e) => setFormData({ ...formData, stumitidref: e.target.value })}
              required
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
            >
              <option value="">-- auswählen --</option>
              {mitarbeiter.map(mit => (
                <option key={mit.id} value={mit.id}>
                  {mit.nachname} {mit.vorname}
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: '1 1 200px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>Auftrag:</label>
            <select
              value={formData.stuaufidref}
              onChange={(e) => setFormData({ ...formData, stuaufidref: e.target.value })}
              required
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
            >
              <option value="">-- auswählen --</option>
              {auftraege.map(auf => (
                <option key={auf.id} value={auf.id}>
                  {auf.nummer} - {auf.bezeichnung}
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: '1 1 200px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>Stundenart:</label>
            <select
              value={formData.stusaridref}
              onChange={(e) => setFormData({ ...formData, stusaridref: e.target.value })}
              required
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
            >
              <option value="">-- auswählen --</option>
              {stundenarten.map(art => (
                <option key={art.id} value={art.id}>
                  {art.bezeichnung}
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: '1 1 150px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>Anzahl Stunden:</label>
            <input
              type="number"
              step="0.01"
              value={formData.stuanzahl}
              onChange={(e) => setFormData({ ...formData, stuanzahl: e.target.value })}
              required
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>
        </div>
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px' }}>Beschreibung:</label>
          <textarea
            value={formData.stubeschrieb}
            onChange={(e) => setFormData({ ...formData, stubeschrieb: e.target.value })}
            rows={3}
            style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
          />
        </div>
        <div style={formButtonRowStyle}>
          <button
            type="submit"
            style={{
              ...formButtonStyle,
              background: '#007bff',
              color: 'white'
            }}
          >
            Speichern
          </button>
          <button
            type="button"
            onClick={onCancel}
            style={{
              ...formButtonStyle,
              background: '#6c757d',
              color: 'white'
            }}
          >
            Abbrechen
          </button>
        </div>
      </form>
    </div>
  );
};

// Stundenart Formular
const StundenartForm = ({ stundenart, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    bezeichnung: stundenart?.bezeichnung || '',
    stundensatz: stundenart?.stundensatz || 0
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div>
      <h3 style={{ marginTop: 0 }}>{stundenart ? 'Stundenart bearbeiten' : 'Neue Stundenart'}</h3>
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px' }}>Bezeichnung:</label>
          <input
            type="text"
            value={formData.bezeichnung}
            onChange={(e) => setFormData({ ...formData, bezeichnung: e.target.value })}
            required
            style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
          />
        </div>
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px' }}>Stundensatz:</label>
          <input
            type="number"
            step="0.01"
            value={formData.stundensatz}
            onChange={(e) => setFormData({ ...formData, stundensatz: e.target.value })}
            required
            style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
          />
        </div>
        <div style={formButtonRowStyle}>
          <button 
            type="submit" 
            style={{
              ...formButtonStyle,
              background: '#007bff',
              color: 'white'
            }}
          >
            Speichern
          </button>
          <button 
            type="button" 
            onClick={onCancel}
            style={{
              ...formButtonStyle,
              background: '#6c757d',
              color: 'white'
            }}
          >
            Abbrechen
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminPanel;

